import React from 'react'
import { useAnimations } from '@react-three/drei'
import { fixSplitMeshAnimations, mergeSplitAnimations, fixSplitGroupTransforms } from '../managers/AnimationManager.js'
import { debugLog } from '../utils/debug.js'

/**
 * Custom hook that wraps drei's useAnimations with animation track fixes.
 * 
 * This hook automatically applies fixes for:
 * 1. Split animations (morph vs transform tracks separated by exporter)
 * 2. Multi-material meshes (GLTF loader splits meshes, breaking morph target bindings)
 * 
 * @param {THREE.AnimationClip[]} animations - Animation clips from useGLTF
 * @param {THREE.Object3D} scene - The loaded GLB scene
 * @returns {Object} Same return value as useAnimations: { mixer, actions, names, ref, clips }
 */
export function useFixedAnimations(animations, scene)
{
    // Apply animation fixes BEFORE useAnimations creates actions
    // This must happen synchronously during render and before useAnimations is called
    React.useMemo(() =>
    {
        if (scene && animations && animations.length > 0)
        {
            debugLog('[useFixedAnimations] Processing animations before binding...')
            debugLog(`[useFixedAnimations] Found ${animations.length} animation clips:`)

            // Log clip info before fixes
            animations.forEach((clip, i) =>
            {
                const morphTracks = clip.tracks.filter(t => t.name.includes('morphTargetInfluences'))
                const transformTracks = clip.tracks.filter(t => !t.name.includes('morphTargetInfluences'))
                debugLog(`[useFixedAnimations]   [${i}] "${clip.name}": ${clip.tracks.length} tracks (${morphTracks.length} morph, ${transformTracks.length} transform)`)

                if (morphTracks.length > 0)
                {
                    debugLog(`[useFixedAnimations]       Morph tracks:`, morphTracks.map(t => t.name))
                }
            })

            // 1. Merge split animations (e.g., separate morph and transform clips)
            mergeSplitAnimations(animations)

            // 2. Fix morph target tracks for multi-material split meshes
            fixSplitMeshAnimations(scene, animations)

            // THIS BREAKS MULTIPLE MATERIALS
            // 3. Fix split group transform tracks
            // fixSplitGroupTransforms(scene, animations)

            debugLog('[useFixedAnimations] Animation fixes applied.')
        }
        else
        {
            debugLog('[useFixedAnimations] No animations to process.')
        }
    }, [ scene, animations ])

    // Now call useAnimations with the fixed animations
    return useAnimations(animations, scene)
}

export default useFixedAnimations
